// SPDX-License-Identifier: AGPL-3.0-or-later

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch, ApiError } from './client.js';
import { meQueryKey } from './auth.js';
import type { PublicUser } from './types.js';

export interface OrgSignupBody {
  name: string;
  slug: string;
  admin: { email: string; password: string; displayName: string };
}

export interface OrgSignupResponse {
  organizationId: string;
  slug: string;
  adminUserId: string;
}

export function useSignupOrg() {
  return useMutation<OrgSignupResponse, ApiError, OrgSignupBody>({
    mutationFn: (body) => apiFetch('/api/orgs', { method: 'POST', body }),
  });
}

export interface OrgPatchBody {
  name?: string;
  /** Astrum organization id for SSO auto-provisioning; null unlinks. */
  astrumOrgId?: string | null;
  // No magic_link_sender (intakeform-preview-w0rm): the sender is a
  // deployment-level choice read from MAGIC_LINK_SENDER at boot, never from
  // org settings, and the API no longer stores the key.
  settings?: {
    freshness_days?: number;
    brand_name?: string;
    brand_color?: string;
    brand_radius?: 'none' | 'sm' | 'md' | 'lg' | 'full';
  };
}

export function usePatchOrg() {
  const qc = useQueryClient();
  return useMutation<{ organization: unknown }, ApiError, OrgPatchBody>({
    mutationFn: (body) => apiFetch('/api/organizations/me', { method: 'PATCH', body }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: meQueryKey });
    },
  });
}

export interface PendingInvite {
  id: string;
  email: string;
  role: 'clinician' | 'admin' | 'hospital' | 'doctor_office' | 'xray' | 'auxiliary';
  displayName: string | null;
  expiresAt: string;
  createdAt: string;
}

export interface TeamListResponse {
  members: PublicUser[];
  pendingInvites: PendingInvite[];
}

export const teamQueryKey = ['team'] as const;

export function useTeam() {
  return useQuery<TeamListResponse>({
    queryKey: teamQueryKey,
    queryFn: () => apiFetch('/api/users'),
  });
}

export interface InviteBody {
  email: string;
  role: 'clinician' | 'admin' | 'hospital' | 'doctor_office' | 'xray' | 'auxiliary';
  displayName?: string;
}

export interface InviteResponse {
  id: string;
  email: string;
  role: 'clinician' | 'admin' | 'hospital' | 'doctor_office' | 'xray' | 'auxiliary';
  expiresAt: string;
  devLink?: string;
  devJwt?: string;
}

export function useIssueInvite() {
  const qc = useQueryClient();
  return useMutation<InviteResponse, ApiError, InviteBody>({
    mutationFn: (body) => apiFetch('/api/users/invites', { method: 'POST', body }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: teamQueryKey });
    },
  });
}

export function useRevokeInvite() {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, ApiError, string>({
    mutationFn: (inviteId) => apiFetch(`/api/users/invites/${inviteId}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: teamQueryKey });
    },
  });
}

export function useRemoveMember() {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, ApiError, string>({
    mutationFn: (userId) => apiFetch(`/api/users/${userId}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: teamQueryKey });
    },
  });
}

export interface AcceptInviteBody {
  password: string;
  displayName: string;
}

export function useAcceptInvite(token: string | undefined) {
  return useMutation<{ user: PublicUser }, ApiError, AcceptInviteBody>({
    mutationFn: (body) => {
      if (!token)
        throw new ApiError(400, {
          error: { code: 'invalid_token', message: 'Missing invite token.' },
        });
      return apiFetch(`/api/users/invites/${token}/accept`, { method: 'POST', body });
    },
  });
}
